import { useGetInfinite as useGetHcdTlcImmediate } from '@maiia/model/generated/model/api-patient/hooks/patHcdTlcImmediate';
import { useGetInfinite as useGetPublicHcdTlcImmediate } from '@maiia/model/generated/model/api-patient/hooks/patPublicHcdTlcImmediate';
import { useCurrentUser } from '@docavenue/components';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';

import {
  CardHcd,
  ResultPageCardHcd,
} from '@maiia/model/generated/model/api-patient/api-patient';
import { UseInfiniteQueryResult } from '@tanstack/react-query';
import {
  SEARCH_COUNT_PER_PAGE_V2,
  TLC_CARD_REFETCH_INTERVAL,
} from '../constants';
import { queryParams2ApiParams } from '@/components/templates/Search/functions';
import { LIMITATION_ALL_PATIENTS } from '@/components/molecules/FilterBar/TLCPatientLimitationSwitch';

const getNextPageParam = (
  lastPage: ResultPageCardHcd,
  allPages: ResultPageCardHcd[],
) =>
  (lastPage?.total || 0) > SEARCH_COUNT_PER_PAGE_V2 * allPages.length
    ? allPages.length
    : undefined;

const useTlcCards = (mustGetKnownPractitioners: boolean = true) => {
  const router = useRouter();
  const searchParams = React.useMemo(() => {
    const params = queryParams2ApiParams(router.query);

    if (mustGetKnownPractitioners || !params.newPatientFilter) {
      params.newPatientFilter = LIMITATION_ALL_PATIENTS;
      // TODO: remove when the filter newPatientFilter has been implemented in the endpoint /pat-public/hcd/tlc/immediate
      params.patientLimitationFilter = LIMITATION_ALL_PATIENTS;
    } else {
      // TODO: remove when the filter newPatientFilter has been implemented in the endpoint /pat-public/hcd/tlc/immediate
      params.patientLimitationFilter = 'PUBLIC';
    }

    return {
      ...params,
      limit: SEARCH_COUNT_PER_PAGE_V2,
    };
  }, [router.query]);
  const currentUser = useCurrentUser();

  const hcdTlcImmediateQuery = useGetHcdTlcImmediate(
    {
      ...searchParams,
      searchOptions: [
        mustGetKnownPractitioners ? 'KNOWN_PRAT' : 'UNKNOWN_PRAT',
      ],
    },
    {
      getNextPageParam,
      refetchInterval: TLC_CARD_REFETCH_INTERVAL,
      enabled: !!currentUser?.id && Object.keys(router.query).length > 0,
    },
  );

  const publicHcdTlcImmediateQuery = useGetPublicHcdTlcImmediate(
    {
      ...searchParams,
      searchOptions: undefined,
    },
    {
      getNextPageParam,
      refetchInterval: TLC_CARD_REFETCH_INTERVAL,
      enabled:
        !currentUser?.id &&
        !mustGetKnownPractitioners &&
        Object.keys(router.query).length > 0,
    },
  );

  const {
    items,
    total,
    queryResult,
  }: {
    items: CardHcd[];
    total: number;
    queryResult: UseInfiniteQueryResult<ResultPageCardHcd>;
  } = useMemo(() => {
    const data = currentUser?.id
      ? hcdTlcImmediateQuery.data
      : publicHcdTlcImmediateQuery.data;
    const dataTmp = data?.pages.flat() ?? [];
    return {
      items: dataTmp.flatMap(cardHdcData => cardHdcData.items || []),
      total: data?.pages[0].total || 0,
      queryResult: currentUser?.id
        ? hcdTlcImmediateQuery
        : publicHcdTlcImmediateQuery,
    };
  }, [currentUser?.id, hcdTlcImmediateQuery, publicHcdTlcImmediateQuery]);

  return {
    items,
    total,
    queryResult,
  };
};

export default useTlcCards;
